import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, readonly, watch, watchEffect } from 'vue';
import BaseInput from "@/components-UI/BaseInput.vue";
import BasePopup from "@/components-UI/BasePopup.vue";
import ArrowDownSvg from '@/assets/icons/arrow-down.svg';
import CheckDarkSvg from '@/assets/icons/check-dark.svg';
import BaseText from "@/components-UI/BaseText.vue";
import BaseOptions from "@/components-UI/BaseOptions.vue";
import BaseCheckbox from "@/components-UI/BaseCheckbox.vue";
import { getRandomId, showErrorModal, showServerError } from "@/utils";
import store from "@/store";

type UnknownItem = { [key: string]: unknown };

interface BaseAutocompleteProps {
    value?: any,
    chipsValue?: UnknownItem[] | string | number,
    label?: string,
    type?: string,
    placeholder?: string,
    disabled?: boolean,
    readonly?: boolean,
    clearable?: boolean,
    clearableType?: 'inner' | 'out',
    view?: 'border-bottom' | 'outlined' | 'empty',
    size?: 'small' | 'medium' | 'large',
    smallSize?: boolean,
    compactSize?: boolean,
    triggerOnMounted?: boolean,
    position?: string,
    itemValue?: string,
    itemLabel?: string,
    delay?: number,
    addNewValue?: boolean,
    addNewValueText?: string,
    emptyText?: string,
    multiple?: boolean,
    isChip?: boolean,
    maxChips?: number,
    isDeletableChips?: boolean,
    chipMaxWidth?: string,
    chipsTooltipWidth?: string,
    teleportPopupToBody?: boolean,
    apiMethod?: (value: any) => Promise<UnknownItem[]>,
    searchKey?: string,
    isPreloadItems?: boolean,
    searchKey?: 'search' | 'query',
    externalParams?: Record<string, any>,
    minSearchLength?: number,
    /** Текст пункта списка и поля ввода после выбора; не участвует в локальной фильтрации. */
    template?: (item: UnknownItem) => string,
    /** Ключи полей элемента для локальной фильтрации (`getFilteredItems`, multipaste, поиск при установке value). Если не задано — по `itemLabel` и `itemValue`. */
    filterKeys?: string[],
    isMultipaste?: boolean,
    limitMultiPaste?: number,
    isReturnObject?: boolean,
    /** Закрепляет текущего пользователя (account_id ?? id) первым в списке опций и резолвит его как выбранный чип без поиска. */
    pinCurrentUser?: boolean,
    /** Не перефильтровывать выдачу apiMethod на клиенте — доверять серверной фильтрации (filterKeys тогда применяется только к закреплённому пользователю). */
    disableLocalFilter?: boolean,
}


export default /*#__PURE__*/_defineComponent({
  __name: 'BaseAutocomplete',
  props: {
    value: null,
    chipsValue: null,
    label: null,
    type: null,
    placeholder: null,
    disabled: { type: Boolean },
    readonly: { type: Boolean },
    clearable: { type: Boolean },
    clearableType: null,
    view: null,
    size: { default: 'large' },
    smallSize: { type: Boolean },
    compactSize: { type: Boolean },
    triggerOnMounted: { type: Boolean },
    position: { default: 'bottom' },
    itemValue: { default: 'id' },
    itemLabel: { default: 'name' },
    delay: { default: 300 },
    addNewValue: { type: Boolean },
    addNewValueText: { default: 'Добавить новый' },
    emptyText: { default: 'Ничего не найдено' },
    multiple: { type: Boolean },
    isChip: { type: Boolean },
    maxChips: null,
    isDeletableChips: { type: Boolean, default: true },
    chipMaxWidth: null,
    chipsTooltipWidth: null,
    teleportPopupToBody: { type: Boolean },
    apiMethod: null,
    searchKey: { default: 'search' },
    isPreloadItems: { type: Boolean },
    externalParams: null,
    minSearchLength: { default: 1 },
    template: null,
    filterKeys: null,
    isMultipaste: { type: Boolean },
    limitMultiPaste: { default: 100 },
    isReturnObject: { type: Boolean },
    pinCurrentUser: { type: Boolean },
    disableLocalFilter: { type: Boolean }
  },
  emits: [
    'update:value',
    'update:chips',
    'input',
    'focus',
    'blur',
    'clear',
    'change',
    'select',
    'add',
],
  setup(__props: any, { expose, emit: emits }) {

const props = __props as { value?: any, chipsValue?: UnknownItem[] | string | number, label?: string, type?: string, placeholder?: string, disabled?: boolean, readonly?: boolean, clearable?: boolean, clearableType?: 'inner' | 'out', view?: 'border-bottom' | 'outlined' | 'empty', size: 'small' | 'medium' | 'large', smallSize?: boolean, compactSize?: boolean, triggerOnMounted?: boolean, position: string, itemValue: string, itemLabel: string, delay: number, addNewValue?: boolean, addNewValueText: string, emptyText: string, multiple?: boolean, isChip?: boolean, maxChips?: number, isDeletableChips: boolean, chipMaxWidth?: string, chipsTooltipWidth?: string, teleportPopupToBody?: boolean, apiMethod?: (value: any) => Promise<UnknownItem[]>, searchKey: string, isPreloadItems?: boolean, searchKey: 'search' | 'query', externalParams?: Record<string, any>, minSearchLength: number, template?: (item: UnknownItem) => string, filterKeys?: string[], isMultipaste?: boolean, limitMultiPaste: number, isReturnObject?: boolean, pinCurrentUser?: boolean, disableLocalFilter?: boolean }




const inputValue = ref('');
const inputText = readonly(inputValue);
const chips = ref<UnknownItem[]>([]);
const isMenuOpen = ref<boolean>(false);
const listItems = ref<UnknownItem[]>([]);
const selectedItem = ref<UnknownItem | null>(null);
const selectedItemIndex = ref<number | null>(null);
const loading = ref<boolean>(false);
const popupRef = ref<BasePopup | null>(null);
const unfoundedItems = ref<UnknownItem[]>([]);
const isValueWasSet = ref(false);

const itemMatchesClientFilter = (item: UnknownItem, needle: string): boolean => {
    const q = needle.trim().toLowerCase();
    if (!q) return true;

    const keys = props.filterKeys;
    if (keys?.length) {
        return keys.some((k) => String(item[k] ?? '').toLowerCase().includes(q));
    }

    const label = String(item[props.itemLabel] ?? '').toLowerCase();
    const valueStr = String(item[props.itemValue] ?? '').toLowerCase();
    const templateStr = props.template ? String(props.template(item)).toLowerCase() : '';
    return (
        label.includes(q) ||
        valueStr.includes(q) ||
        (templateStr.length > 0 && templateStr.includes(q))
    );
};

const updateMenuOpen = (value: boolean) => {
    isMenuOpen.value = value;
};
const focusHandler = (value: boolean) => {
    emits('focus', value);
};
const blurHandler = (value: boolean) => {
    emits('blur', value);
};

const formatChips = computed<UnknownItem[]>(() => {
    return chips.value.map((el: UnknownItem) => el[props.itemValue] || el[props.itemLabel]);
});

const isSelectRow = (item: UnknownItem) => computed(() => {
    return !props.multiple && (
        (selectedItem.value?.[props.itemValue] === item[props.itemValue]) ||
        (inputValue.value === item[props.itemValue])
    );
});

const isCheckedRow = (item: UnknownItem) => computed<boolean>(() => {
    if (!props.multiple) return false;

    return chips.value.some((chip: UnknownItem) =>
        String(chip[props.itemValue]) === String(item[props.itemValue]),
    );
});

const getFormattedInputValue = computed<string>(() => {
    const found = foundItem(inputValue.value, props.itemValue);
    const isSetFound = !props.multiple && found && String(found[props.itemLabel]) === String(inputValue.value);
    return isSetFound ? found[props.itemLabel] : inputValue.value;
});

const isAddNewValue = computed<boolean>(() => {
    return props.addNewValue &&
        !foundItem(inputValue.value, props.itemLabel) &&
        inputValue.value;
});

const foundItem = (search?: string, prop?: string): UnknownItem | undefined => {
    if (!search || !prop) return;

    return listItems.value.find((item: UnknownItem) =>
        String(item[prop]) === String(search),
    );
};

const pinnedUserItem = computed<UnknownItem | null>(() => {
    if (!props.pinCurrentUser) return null;
    const user = store.getters['authModule/GET_USER'];
    const id = user?.account_id ?? user?.id;
    if (id == null) return null;
    return {
        id,
        user_id: user.id,
        first_name: user.first_name,
        last_name: user.last_name,
        email: user.email,
        name: `${id} ${user.first_name ?? ''} ${user.last_name ?? ''}`.trim(),
        [props.itemValue]: id,
    };
});

const getFilteredItems = computed<UnknownItem[]>(() => {
    // Серверный поиск (apiMethod) уже отфильтровал выдачу по запросу. Повторная фильтрация на
    // клиенте может скрыть валидные результаты (совпадение по полю, которого нет в filterKeys),
    // поэтому при disableLocalFilter доверяем серверу. Закреплённый пользователь добавляется
    // локально, поэтому он всегда сверяется с запросом отдельно.
    const base = (props.disableLocalFilter && props.apiMethod)
        ? listItems.value
        : listItems.value.filter((item: UnknownItem) => itemMatchesClientFilter(item, inputValue.value));

    const pinned = pinnedUserItem.value;
    if (!pinned) return base;

    const rest = base.filter(
        (item: UnknownItem) => String(item[props.itemValue]) !== String(pinned[props.itemValue]),
    );
    return itemMatchesClientFilter(pinned, inputValue.value) ? [pinned, ...rest] : rest;
});

const showOptions = computed<boolean>(() => {
    if (loading.value) return false;
    if (pinnedUserItem.value) return !!getFilteredItems.value.length;
    return !!listItems.value.length;
});

const pinnedItemIndex = computed<number>(() => {
    const pinned = pinnedUserItem.value;
    if (!pinned) return -1;
    const items = getFilteredItems.value;
    return items.length && String(items[0]?.[props.itemValue]) === String(pinned[props.itemValue]) ? 0 : -1;
});

const pinnedDividerIndex = computed<number>(() => {
    return pinnedItemIndex.value === 0 && getFilteredItems.value.length > 1 ? 0 : -1;
});

const debounce = (fn) => {
    let timeoutId;
    return (...args) => {
        if (timeoutId) {
            clearTimeout(timeoutId);
        }
        timeoutId = setTimeout(() => fn(...args), props.delay);
    };
};

const isMultipaste = (collection: string[]) => {
    if (!props.isMultipaste || collection.length <= 1) return false;

    const isEmail = /^[\w.-]+@([\w-]+\.)+[\w-]{2,}$/;
    const allEmails = collection.every(item => isEmail.test(item));
    const allNumbers = collection.every(item => !isNaN(Number(item)));
    return allEmails || allNumbers;
};

const executeSearch = (value: string) => {
    const collection = value.trim().split(/[ ,]+/).filter(Boolean);

    if (isMultipaste(collection)) {
        multipleSearch(collection);
    } else {
        fetchData(value);
    }
};

const debouncedSearch = debounce(executeSearch);

const searchHandler = (value: string) => {
    inputValue.value = value;
    isMenuOpen.value = true;

    if (props.apiMethod) {
        if (!value || value.length < props.minSearchLength) {
            // Не очищаем список и не делаем запрос - показываем весь ранее загруженный список
            return;
        }
        debouncedSearch(value);
    } else {
        handleUpdate(value);
    }
};

const fetchData = async (value?: string) => {
    try {
        loading.value = true;
        const items = await props.apiMethod({ [props.searchKey]: value, ...props.externalParams });
        listItems.value = items.data ?? items;
        popupRef?.value.updatePosition();
    } catch (e) {
        showServerError(e);
    } finally {
        loading.value = false;
    }
};

const multipleSearch = (tokens: string[]) => {
    const collection = props.limitMultiPaste ? tokens.slice(0, props.limitMultiPaste - 1) : tokens;
    multipleFetchData(collection);
};

const multipleFetchData = async (tokens: string[]) => {
    try {
        loading.value = true;
        const requests = tokens.map(token => props.apiMethod({ [props.searchKey]: token }));
        const responses = await Promise.all(requests);

        responses.forEach((res, index) => {
            const { data } = res;
            const token = tokens[index];
            if (data.length) {
                const item = data.filter((row: UnknownItem) => itemMatchesClientFilter(row, token));
                if (item.length) {
                    handleSelect({ item: item[0], index, isMultipaste: true });
                } else {
                    unfoundedItems.value.push(token);
                }
            } else {
                unfoundedItems.value.push(token);
            }
        });
        if (unfoundedItems.value.length) {
            const ids = unfoundedItems.value.join(', ');
            const message = `Не найдены: ${ids}`;
            showErrorModal(message);
        }
    } catch (e) {
        showServerError(e);
    } finally {
        loading.value = false;
    }
};

const handleUpdate = (value: string | number[]): void => {
    if (props.multiple || props.isChip) {
        emits('update:value', value);
    }

    if (!value) {
        emits('update:value', '');
        setSelectedItem();
    }
};

const setDefaultValue = async () => {
    if (!props.value || (props.multiple && !props.value.length) || !props.apiMethod) return;
    if (props.multiple) {
        for (const item of props.value) {
            await fetchData(item);
        }
        const pinned = pinnedUserItem.value;
        const pool = pinned
            ? [pinned, ...listItems.value.filter((i: UnknownItem) => String(i[props.itemValue]) !== String(pinned[props.itemValue]))]
            : listItems.value;
        const items = pool.filter((item: UnknownItem) => props.value.includes(item[props.itemValue]));
        items.forEach((item: UnknownItem, index: number) => handleSelect({ item, index }));
    } else {
        await fetchData(props.value);
        const item = listItems.value.filter((row: UnknownItem) =>
            itemMatchesClientFilter(row, String(props.value)),
        );

        if (!item.length) return;
        handleSelect({ item: item[0], index: 0 });
    }
    isValueWasSet.value = true;
};

const handleSelect = (
    { item, index, isAdd, isMultipaste }:
    { item: UnknownItem, index: number, isAdd?: boolean, isMultipaste?: boolean },
): void => {
    isValueWasSet.value = true;
    setSelectedItem(item, index);

    if (props.isChip) {
        chips.value = [item];
        inputValue.value = '';
        isMenuOpen.value = false;
        emits('update:value', '');
        emits('update:chips', item[props.itemValue] || item[props.itemLabel]);
        emits('select', { item, index });
        return;
    }

    if (props.multiple) {
        if (isMultipaste) {
            chips.value.push({ ...item, checked: true });
            listItems.value.push(item);
        } else {
            const chipIndex = chips.value.findIndex(chip =>
                String(chip[props.itemValue]) === String(item[props.itemValue]),
            );

            if (chipIndex > -1) {
                chips.value.splice(chipIndex, 1);
                item.checked = false;
            } else {
                chips.value.push({ ...item, checked: true });
            }
        }

        inputValue.value = '';
        emits('update:value', '');
        emits('update:chips', formatChips.value);
        emits('select', { item, index });
        return;
    }

    inputValue.value = props.template ? props.template(item) : item[props.itemLabel];
    isMenuOpen.value = false;
    const value = props.isReturnObject ? item : isAdd ? item[props.itemLabel] : item[props.itemValue] || item[props.itemLabel];
    emits('update:value', value);
    emits('select', { item, index });
};

const handleDeleteChip = (): void => {
    emits('update:chips', formatChips.value);
    setSelectedItem();
};

const handleAdd = (): void => {
    const tempId = getRandomId();
    const item = { [props.itemLabel]: inputValue.value, [props.itemValue]: tempId };
    const length = listItems.value.push(item);

    handleSelect({ item, index: length - 1, isAdd: true });
    emits('add', { item, index: length - 1 });
};

const handleInput = (value: string): void => {
    if (!value) {
        handleClear();
    }
};

let isClearing = false;
const handleClear = (): void => {
    // очистка через крестик приходит дважды: BaseInput эмитит и input:'' (→ handleInput
    // → handleClear), и clear (→ handleClear). Дедупим в пределах одного тика.
    if (isClearing) return;
    isClearing = true;
    setTimeout(() => { isClearing = false; }, 0);

    emits('clear');
    emits('update:value', '');
    emits('select', { item: null });
    emits('change', '');
    setSelectedItem();
    if (props.multiple) {
        chips.value = [];
    }
};

const setSelectedItem = (item: UnknownItem | null = null, index: number | null = null): void => {
    selectedItem.value = item;
    selectedItemIndex.value = index;
};

watchEffect(() => {
    (props.triggerOnMounted && !isValueWasSet.value) && setDefaultValue();
    (props.isPreloadItems && props.apiMethod) && fetchData();
});

watch(() => props.value, () => {
    if (!props.value) {
        inputValue.value = '';
        setSelectedItem();
    }
});

watch(() => props.chipsValue?.length, (count: number) => {
    if (!count) {
        chips.value = [];
    }
}, { deep: true, immediate: true });

expose({ inputText });

return { __sfc: true,props, emits, inputValue, inputText, chips, isMenuOpen, listItems, selectedItem, selectedItemIndex, loading, popupRef, unfoundedItems, isValueWasSet, itemMatchesClientFilter, updateMenuOpen, focusHandler, blurHandler, formatChips, isSelectRow, isCheckedRow, getFormattedInputValue, isAddNewValue, foundItem, pinnedUserItem, getFilteredItems, showOptions, pinnedItemIndex, pinnedDividerIndex, debounce, isMultipaste, executeSearch, debouncedSearch, searchHandler, fetchData, multipleSearch, multipleFetchData, handleUpdate, setDefaultValue, handleSelect, handleDeleteChip, handleAdd, handleInput, isClearing, handleClear, setSelectedItem, readonly, BaseInput, BasePopup, ArrowDownSvg, CheckDarkSvg, BaseText, BaseOptions, BaseCheckbox }
}

})